import { useEffect, useRef, useState } from "react";
import { Button, Segmented } from "antd";
import { useTranslation } from "react-i18next";

import { Body } from "../../../components/main-body/body";
import "./notifications.css";
import useNotification from "../../../components/notification/notification";
import ApiEmailReminders from "../../../api/email-reminders";
import ApiIncompleteTasksReminder from "../../../api/incomplete-tasks-reminder";
import ApiProfileManagement from "../../../api/profile-management";
import ReminderList from "../../../components/reminder-time/reminder-list";
import { FormSwitch } from "../../../components/form/form";
import {
  remindersFromSettings,
  settingsFromReminders,
} from "./reminder-settings-mapping";

// LF360-1896: on/off as two halves of one control rather than a switch, which is
// what the design asks for and reads the same on both sections.
const ON_OFF = [
  { label: "Enabled", value: "enabled" },
  { label: "Disabled", value: "disabled" },
];

// LF360-1936 — what the switch above them covers. A project that turns email on
// receives a great deal of it, so an admin says which kinds go out. The keys are
// the backend's own field names, which keeps the payload a copy of this state
// rather than a translation of it.
const EMAIL_REMINDER_KINDS = [
  { field: "emailRemindersIncompleteTasks", label: "Incomplete tasks" },
  { field: "emailRemindersWeeklyTasks", label: "Weekly tasks (daily reminder)" },
  { field: "emailRemindersTriggeredTasks", label: "Triggered tasks (daily reminder)" },
  { field: "emailRemindersTriggeredUpcoming", label: "Triggered tasks (upcoming)" },
  // A task an admin assigned to one subject by hand. It belongs to no schedule,
  // so it was none of the four this list started with and followed the switch
  // alone — an admin could clear every box here and still see these go out.
  { field: "emailRemindersPersonalTasks", label: "Personally assigned tasks" },
];

const emailKindsFrom = (data = {}) =>
  Object.fromEntries(
    EMAIL_REMINDER_KINDS.map(({ field }) => [field, data[field] === true])
  );

/**
 * LF360-1868 — when the project reminds subjects about tasks they have not
 * finished but still can.
 *
 * The times are edited with the same component tasks use, so a reminder means the
 * same thing wherever an admin sets one, and several reminders a day work here for
 * the same reason they work there.
 *
 * LF360-1914 added the second section: whether reminders about tasks also go out
 * by email. One switch for the project, under the one edit mode this screen has,
 * so Save means the whole screen.
 *
 * LF360-1896 redressed it. The two-column form gave way to the stacked sections
 * the design asks for, the switches became on/off pairs, and the reminder times
 * moved to the shared picker in src/components/reminder-time — where a row says
 * what it does in words and opens one control for both kinds. The row that used
 * to ask which profile field the FIRST reminder counted from went with it: every
 * reminder now names its own inside the picker, so there is nothing left for a
 * second control to disagree with.
 */
const Notifications = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [isEdit, setIsEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [timeFields, setTimeFields] = useState([]);
  const [emailReminders, setEmailReminders] = useState(false);
  const [emailKinds, setEmailKinds] = useState(emailKindsFrom());
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [initial, setInitial] = useState({
    enabled: true,
    reminders: [],
    emailReminders: false,
    emailKinds: emailKindsFrom(),
  });
  const popupContainerRef = useRef(null);

  // Only a reminder counted from a time can be counted from a profile answer, so
  // the picker offers the time questions and nothing else.
  const loadTimeFields = () => {
    ApiProfileManagement.getProfiles().then(({ data }) => {
      setTimeFields(
        (data.subjectProfileFields || [])
          .filter((field) => field.inputType === "message:time")
          .map((field) => ({ value: field.id, label: field.title }))
      );
    });
  };

  // Both sections or neither. What this screen shows when it has not heard back
  // is indistinguishable from a project that reminds at no particular time and
  // does not email: saving that over real settings would quietly switch a
  // project's reminders off. So editing waits until both answers are in, and a
  // failure says so and offers to ask again.
  const load = () => {
    setLoadFailed(false);

    return Promise.all([
      ApiIncompleteTasksReminder.get(),
      ApiEmailReminders.get(),
    ]).then(([r, email]) => {
      const settings = r.data.settings || {};
      const list = remindersFromSettings(settings);
      const next = {
        enabled: settings.enabled !== false,
        reminders: list,
        emailReminders: email.data.emailRemindersEnable === true,
        emailKinds: emailKindsFrom(email.data),
      };

      setEnabled(next.enabled);
      setReminders(next.reminders);
      setEmailReminders(next.emailReminders);
      setEmailKinds(next.emailKinds);
      setInitial(next);
      setLoaded(true);
    }).catch(() => {
      setLoaded(false);
      setLoadFailed(true);
    });
  };

  useEffect(() => {
    load();
    loadTimeFields();
  }, []);

  const cancel = () => {
    setEnabled(initial.enabled);
    setReminders(initial.reminders);
    setEmailReminders(initial.emailReminders);
    setEmailKinds(initial.emailKinds);
    setIsEdit(false);
    setHasChanges(false);
  };

  const save = async () => {
    try {
      const [{ data }, email] = await Promise.all([
        ApiIncompleteTasksReminder.update({
          enabled,
          // Every reminder carries the field it counts from; the first one's is
          // written into the settings' own column by settingsFromReminders.
          ...settingsFromReminders(reminders),
        }),
        // Sent whether or not it changed: the endpoint compares under a lock and
        // does nothing — not even an audit entry — when the answer is the same.
        // All five together. The endpoint treats an absent field as unchanged
        // rather than off, so sending the lot is what makes an unticked box mean
        // unticked.
        ApiEmailReminders.update({
          emailRemindersEnable: emailReminders,
          ...emailKinds,
        }),
      ]);

      // Read back rather than kept: the backend gives every promoted reminder a
      // real id, and the next edit has to work with those, not the sentinel the
      // editor used.
      const saved = remindersFromSettings(data.settings || {});
      const savedEmail = email.data.emailRemindersEnable === true;
      const savedKinds = emailKindsFrom(email.data);

      setReminders(saved);
      setEmailReminders(savedEmail);
      setEmailKinds(savedKinds);
      setInitial({
        enabled: data.settings?.enabled !== false,
        reminders: saved,
        emailReminders: savedEmail,
        emailKinds: savedKinds,
      });
      setIsEdit(false);
      setHasChanges(false);

      openNotification({
        type: "success",
        message: <p>{t("notifications.saved", "Notification settings saved")}</p>,
      });
    } catch (error) {
      // The two sections are two requests, and one can land while the other
      // fails. Rather than leaving the screen showing what was typed — half of
      // which is now stored and half of which is not — it is read back, so what
      // is on screen is what the project actually has.
      await load();

      openNotification({
        type: "error",
        message: (
          <p>
            {error?.response?.data?.error ||
              t("notifications.saveFailed", "Failed to save notification settings")}
          </p>
        ),
      });
    }
  };

  const onRemindersChange = (next) => {
    setReminders(next);
    setHasChanges(true);
  };

  const onEnabledChange = (checked) => {
    setEnabled(checked);
    setHasChanges(true);
  };

  return (
    // `scroll` gives this screen a scrolling area of its own. Body is
    // overflow: hidden at 100vh, so without it a card taller than the viewport is
    // simply cut off — and a project with several reminders is taller than the
    // viewport. The reminders at the bottom could not be reached at all.
    <Body
      header={{
        title: t("notifications.title", "Notifications"),
        // The design puts Save and Cancel in the page header, beside the title,
        // and the header already knows how to draw them — the same pair the
        // mobile home page and project management use.
        editable: loaded,
        isEditing: isEdit,
        onToggleEdit: () => setIsEdit(true),
        onSave: save,
        onCancelEdit: cancel,
        saveDisabled: !hasChanges,
      }}
      scroll
    >
      <div className="notifications-container" ref={popupContainerRef}>
        {loadFailed && (
          <div className="notifications-error">
            <p className="notifications-hint">
              {t(
                "notifications.loadFailed",
                "These settings could not be loaded, so they are not shown and cannot be edited — saving now would write over whatever the project actually has."
              )}
            </p>

            <Button onClick={load}>{t("notifications.retry", "Try again")}</Button>
          </div>
        )}

        {/* LF360-1960 — the title leads, the explanation follows it. Two
            sections, each a title, its sentence, its on/off pair and its
            contents; the reminder times belong to the first of them rather than
            standing under a heading of their own. That is what removed the need
            for a rule between the groups: there is no longer a third heading to
            read as a peer of the other two. */}
        <section className="notifications-section">
          <h2 className="notifications-section__title">
            {t("notifications.incompleteTasks", "Incomplete tasks reminder")}
          </h2>

          <p className="notifications-hint">
            {t(
              "notifications.incompleteTasksHint",
              "Subjects are reminded of tasks from previous days that they have not completed and can still complete. Nothing is sent when there is nothing waiting."
            )}
          </p>

          {/* role="group" so each on/off pair has a name of its own: both of
              them are labelled Enabled and Disabled, and without it neither a
              screen reader nor a test can say which is which. */}
          <div
            className="notifications-section__control"
            role="group"
            aria-label={t("notifications.incompleteTasks", "Incomplete tasks reminder")}
          >
            <Segmented
              block
              // Named explicitly rather than left to antd's generated id: the
              // two pairs on this screen are radio groups, and sharing a name
              // would make checking one uncheck the other.
              name="incomplete-tasks-reminder"
              className="lf-segmented notifications-toggle"
              options={ON_OFF}
              value={enabled ? "enabled" : "disabled"}
              onChange={(value) => onEnabledChange(value === "enabled")}
              disabled={!isEdit}
            />
          </div>

          <ReminderList
            value={reminders}
            onChange={onRemindersChange}
            readOnly={!isEdit}
            triggerFields={timeFields}
            // The project either reminds at some time or is switched off above;
            // an empty list would be a third state meaning the same as "off".
            minimum={1}
            popupContainerRef={popupContainerRef}
          />
        </section>

        {/* LF360-1914, redressed with the rest of the screen: a project either
            sends its task reminders by email as well, or it does not. */}
        <section className="notifications-section">
          <h2 className="notifications-section__title">
            {t("notifications.emailReminders", "Email reminders")}
          </h2>

          <p className="notifications-hint">
            {t(
              "notifications.emailRemindersHint",
              "Reminders about tasks are also sent by email, in the same words the app shows. LifeBot messages are never emailed."
            )}
          </p>

          <div
            className="notifications-section__control"
            role="group"
            aria-label={t("notifications.emailReminders", "Email reminders")}
          >
            <Segmented
              block
              name="email-reminders"
              className="lf-segmented notifications-toggle"
              options={ON_OFF}
              value={emailReminders ? "enabled" : "disabled"}
              onChange={(value) => {
                setEmailReminders(value === "enabled");
                setHasChanges(true);
              }}
              disabled={!isEdit}
            />
          </div>

          {/* LF360-1936 — which kinds the switch above covers. Kept visible while
              the switch is off, rather than hidden: an admin needs to see what
              turning it back on would send before they turn it on. Disabled
              instead, because the choice means nothing until then.

              LF360-1960 made them switches in a bordered list rather than
              checkboxes in a bare column. The legend went with it: the section
              title and its sentence already say what these are, and the design
              has nothing between them and the rows.

              Still a fieldset, and not for looks — `disabled` on it disables
              every control inside, so the condition is written once rather than
              on each of five rows. The rows pass `readOnly` as well because antd
              styles a switch from its own prop; the fieldset makes it true, the
              prop makes it look true. */}
          <fieldset
            className={
              emailReminders
                ? "notifications-kinds"
                : "notifications-kinds notifications-kinds--inert"
            }
            disabled={!isEdit || !emailReminders}
            // The legend is gone from the design, but the group still needs a
            // name: five switches in a row say nothing about what they are for
            // to somebody who cannot see the section title above them.
            aria-label={t("notifications.emailRemindersFor", "Send email reminders for:")}
          >
            {EMAIL_REMINDER_KINDS.map(({ field, label }) => {
              const name = t(`notifications.emailKind.${field}`, label);

              return (
                <div
                  key={field}
                  className={
                    emailKinds[field]
                      ? "notifications-kinds__item"
                      : "notifications-kinds__item notifications-kinds__item--off"
                  }
                >
                  <FormSwitch
                    checked={emailKinds[field]}
                    readOnly={!isEdit || !emailReminders}
                    ariaLabel={name}
                    onChange={(checked) => {
                      setEmailKinds((prev) => ({ ...prev, [field]: checked }));
                      setHasChanges(true);
                    }}
                  />

                  <span className="notifications-kinds__label">{name}</span>
                </div>
              );
            })}
          </fieldset>
        </section>
      </div>
    </Body>
  );
};

export default Notifications;
